import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, LogOut } from "lucide-react";

const TIMEOUT_MS = 30 * 60 * 1000; // total inactivity window
const WARN_MS = 60 * 1000;         // show warning 1 minute before logout

// Signs admins out after inactivity, with a 1-minute "stay signed in" warning.
export default function InactivityLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownInt = useRef(null);
  const warningRef = useRef(false);
  const stayRef = useRef(() => {});
  const logoutRef = useRef(() => {});
  const [warning, setWarning] = useState(false);
  const [seconds, setSeconds] = useState(60);

  useEffect(() => {
    const isAdmin = user && typeof user === "object" && user.role === "admin";
    if (!isAdmin) return;

    const clearAll = () => {
      clearTimeout(warnTimer.current);
      clearTimeout(logoutTimer.current);
      clearInterval(countdownInt.current);
    };

    const doLogout = async () => {
      clearAll();
      warningRef.current = false;
      setWarning(false);
      await logout();
      toast.info("Signed out due to inactivity");
      navigate("/login", { replace: true });
    };

    const showWarning = () => {
      warningRef.current = true;
      setSeconds(Math.round(WARN_MS / 1000));
      setWarning(true);
      countdownInt.current = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
      logoutTimer.current = setTimeout(doLogout, WARN_MS);
    };

    const reset = () => {
      clearAll();
      warningRef.current = false;
      setWarning(false);
      warnTimer.current = setTimeout(showWarning, TIMEOUT_MS - WARN_MS);
    };

    const onActivity = () => { if (!warningRef.current) reset(); };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    reset();

    // expose the reset for the dialog button
    stayRef.current = reset;
    logoutRef.current = doLogout;

    return () => {
      clearAll();
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user, logout, navigate]);

  const isAdmin = user && typeof user === "object" && user.role === "admin";
  if (!isAdmin) return null;

  return (
    <Dialog open={warning} onOpenChange={(o) => { if (!o) stayRef.current(); }}>
      <DialogContent data-testid="inactivity-warning">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#CC5A3A]">
            <Clock className="w-5 h-5" /> Still there?
          </DialogTitle>
          <DialogDescription>
            You'll be signed out in <strong data-testid="inactivity-countdown">{seconds}</strong> second{seconds === 1 ? "" : "s"} due to inactivity. This protects sensitive data on shared computers.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            onClick={() => logoutRef.current()}
            data-testid="inactivity-logout-now"
            className="text-sm py-2 px-4 rounded-full border border-[#1E3F33]/20 text-[#1E3F33] hover:bg-[#1E3F33]/5 inline-flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Log out now
          </button>
          <button
            onClick={() => stayRef.current()}
            data-testid="inactivity-stay"
            className="text-sm py-2 px-4 rounded-full bg-[#2D6A4F] text-white hover:bg-[#245741]"
          >
            Stay signed in
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
